// Copyright © 2021 Move Closer
import { computed, defineComponent, toRefs } from '@vue/composition-api';
import { SizeMap } from '../../_composables';
import { useInputControl } from '../../_abstract';
import { BootstrapFormError } from '../FormError';
import { BootstrapIcon } from '../Icon';
import { bootstrapInputProps } from './Input.hooks';
import { IconPosition } from './Input.contracts';
import { bootstrapInputSizeRegistry } from './Input.config';
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const BootstrapInput = defineComponent({
    name: 'BootstrapInput',
    components: { FormError: BootstrapFormError, Icon: BootstrapIcon },
    props: bootstrapInputProps,
    emits: ['blur', 'focus', 'icon-click'],
    setup(props, ctx) {
        const { size, iconPosition } = toRefs(props);
        const { hasErrors, sizeClass, validationClass, value: _model } = useInputControl(props, ctx, bootstrapInputSizeRegistry);
        /**
         * Determines the size of the prepended icon
         */
        const iconSize = computed(() => size.value === SizeMap.Medium ? SizeMap.Small : SizeMap.Medium);
        /**
         * Determines the CSS class for icon positioning
         */
        const iconPositionClass = computed(() => {
            return iconPosition.value === IconPosition.Right ? '--with-icon-right' : '--with-icon';
        });
        /**
         * Handle icon click event
         */
        const onIconClick = () => {
            ctx.emit('icon-click');
        };
        return { hasErrors, iconSize, iconPositionClass, _model, sizeClass, validationClass, onIconClick };
    },
    template: `
    <div class="form-input" :class="[sizeClass, icon ? iconPositionClass : '', validationClass ? 'has-validation' : '', { 'has-errors': hasErrors }]">

      <!-- Label -->
      <label v-if="label" :for="$id()" class="form-input__label form-label" :class="{ 'has-errors': hasErrors }">
        {{ label }}<span v-if="required" class="form-label__asterisk">&nbsp;*</span>
      </label>

      <!-- Input wrapper for icon positioning -->
      <div class="form-input__wrapper">
        <!-- Input element -->
        <input v-model="_model" v-bind="{ autocomplete, disabled, name, readonly, required, type }" :id="$id()"
               class="form-input__control form-control" :class="validationClass"
               @focus="() => $emit('focus')" @blur="() => $emit('blur')"
               :placeholder="placeholder" :aria-label="label" >

        <!-- Icon -->
        <button v-if="icon" type="button" class="form-input__icon"
             :class="{ 'form-input__icon--clickable': true }"
             @click="onIconClick">
          <Icon :name="icon" :size="iconSize" />
        </button>
      </div>

      <!-- Validation errors -->
      <FormError v-for="error in errors" :key="$id(error)" class="form-input__error">
        {{ error }}
      </FormError>

    </div>
  `
});
